import { useQueries } from 'convex/react';
import { getFunctionName, type FunctionArgs, type FunctionReference, type FunctionReturnType } from 'convex/server';
import { ConvexError } from 'convex/values';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { convexClient } from '../lib/convex';
import { canPromptInstall, onInstallPromptChange } from '../lib/install';
import { registerServiceWorker } from '../lib/sw';

/**
 * Marks the page as member side (phone-sized type in styles.css), registers the service worker,
 * and reconnects at once when the page comes back from the back-forward cache.
 */
export function useLauncherPage(): void {
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.surface = 'launcher';
    void registerServiceWorker();
    window.addEventListener('pageshow', reconnectAfterRestore);
    return () => {
      delete root.dataset.surface;
      window.removeEventListener('pageshow', reconnectAfterRestore);
    };
  }, []);
}

type SocketInternals = { sync?: { webSocketManager?: { closeAndReconnect?: (reason: string) => void } } };

/**
 * A page restored from the back-forward cache keeps its Convex client, but its socket may be gone:
 * closed (the client then waits out its reconnect backoff) or silently dead while still reported
 * as connected (noticed only after the client's 60 s inactivity timeout). Either way, open a new
 * one now; reconnecting resubscribes every query. `online` is the client's public path for the
 * first case; the second needs the client's own `closeAndReconnect` (convex 1.49), skipped if absent.
 */
function reconnectAfterRestore(e: PageTransitionEvent): void {
  if (!e.persisted) return;
  if (!convexClient.connectionState().isWebSocketConnected) {
    window.dispatchEvent(new Event('online'));
    return;
  }
  (convexClient as unknown as SocketInternals).sync?.webSocketManager?.closeAndReconnect?.('pageshow');
}

/** False until `ms` after mount: for hints that should not flash on fast connections. */
export function useDelayed(ms: number): boolean {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDone(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return done;
}

export type QueryState<T> = { data: T | undefined; error: Error | undefined };

/**
 * Like useQuery, but a failing query (e.g. NOT_FOUND, a revoked token) comes back
 * as `error` instead of throwing during render.
 */
export function useSafeQuery<Q extends FunctionReference<'query'>>(
  query: Q,
  args: FunctionArgs<Q> | 'skip',
): QueryState<FunctionReturnType<Q>> {
  const argsKey = JSON.stringify(args);
  const queries = useMemo(
    (): Record<string, { query: Q; args: FunctionArgs<Q> }> => (args === 'skip' ? {} : { q: { query, args } }),
    [getFunctionName(query), argsKey],
  );
  const value: unknown = useQueries(queries).q;
  if (value instanceof Error) return { data: undefined, error: value };
  return { data: value as FunctionReturnType<Q> | undefined, error: undefined };
}

/** One query per key (e.g. per circle slug), each with its own args. Errors are returned, not thrown. */
export function useSafeQueries<Q extends FunctionReference<'query'>>(
  query: Q,
  argsByKey: Record<string, FunctionArgs<Q>>,
): Record<string, QueryState<FunctionReturnType<Q>>> {
  const argsKey = JSON.stringify(argsByKey);
  const queries = useMemo(
    () => Object.fromEntries(Object.entries(argsByKey).map(([k, args]) => [k, { query, args }])),
    [getFunctionName(query), argsKey],
  );
  const values = useQueries(queries) as Record<string, unknown>;
  const out: Record<string, QueryState<FunctionReturnType<Q>>> = {};
  for (const k of Object.keys(argsByKey)) {
    const v = values[k];
    out[k] = v instanceof Error ? { data: undefined, error: v } : { data: v as FunctionReturnType<Q> | undefined, error: undefined };
  }
  return out;
}

/** The `code` of a ConvexError thrown by our functions (lib/errors.ts), or null. */
export function errorCode(err: unknown): string | null {
  if (err instanceof ConvexError) {
    const data = err.data as { code?: unknown } | undefined;
    return typeof data?.code === 'string' ? data.code : null;
  }
  return null;
}

/** Re-renders when the browser offers (or withdraws) a native install prompt. */
export function useCanPromptInstall(): boolean {
  return useSyncExternalStore(onInstallPromptChange, canPromptInstall, () => false);
}
